import React from 'react'
import { useTheme } from './ThemeProvider'

interface NaturalBackgroundProps {
  variant?: 'sky' | 'forest' | 'ocean' | 'meadow'
  opacity?: number
  children: React.ReactNode
}

const NaturalBackground: React.FC<NaturalBackgroundProps> = ({ 
  variant = 'sky', 
  opacity = 0.3,
  children 
}) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const getGradient = () => {
    if (isDark) {
      // Dark mode gradients
      switch (variant) {
        case 'sky':
          return 'from-blue-900/30 via-blue-800/20 to-sage-900/30'
        case 'forest':
          return 'from-sage-900/30 via-sage-800/20 to-sage-900/30'
        case 'ocean':
          return 'from-blue-900/30 via-blue-800/20 to-blue-900/30'
        case 'meadow':
          return 'from-sage-900/30 via-mint-900/20 to-sage-900/30'
        default:
          return 'from-sage-900/30 via-sage-800/20 to-sage-900/30'
      }
    } else {
      // Light mode gradients
      switch (variant) {
        case 'sky':
          return 'from-blue-50/20 via-blue-25/10 to-cream-25/20'
        case 'forest':
          return 'from-sage-50/20 via-mint-25/10 to-sage-25/20'
        case 'ocean':
          return 'from-blue-50/20 via-mint-25/10 to-blue-25/20'
        case 'meadow':
          return 'from-mint-50/20 via-sage-25/10 to-cream-25/20'
        default:
          return 'from-sage-50/20 via-cream-25/10 to-mint-25/20'
      }
    }
  }

  return (
    <div className="relative min-h-screen">
      {/* Natural gradient background */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br ${getGradient()}`}
        style={{ opacity: isDark ? opacity * 1.5 : opacity }}
      />
      
      {/* Subtle texture overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  )
}

export default NaturalBackground